// @givmo/design-system — MaskedInput (a masked TextField variant).
//
// The Flutter `BaseTextField` accepted an optional input mask/formatter (the kit threaded
// `inputFormatters` through). This is the web equivalent: the same labeled, two-state-border,
// focus-glow, inline-error text field as <TextField>, but with the typed value masked as the
// user types (phone numbers, amounts, dates, card numbers, …).
//
// Masking is delegated to `react-imask` behind this THIN wrapper. react-imask is a maintenance-
// stale library, so it is intentionally swappable: callers only see `mask` (a string pattern or
// a passthrough `imaskOptions` object) + `onAccept(value)` — they never import react-imask
// directly. To swap the masking engine later, only this file changes.
//
// a11y / visuals: identical to <TextField> — label = Lato w600 s12, input = Lato w400 s16 / 1.5,
// idle neutral-500 border, focus → blue + focus-glow, error → magenta + inline <ErrorMessage>,
// `aria-invalid` + `aria-describedby` wired the same way. `testId` forwards to the <input>.
import { useId } from 'react';
import { IMaskInput } from 'react-imask';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { ErrorMessage } from './error-message';

export interface MaskedInputProps extends TestIdProps {
  /**
   * The IMask pattern string (e.g. `'+{1} (000) 000-0000'` for a US phone, `'$num'` blocks, …).
   * For non-pattern masks (Number, Date, RegExp) pass `imaskOptions` instead.
   */
  mask?: string;
  /**
   * Escape hatch: raw IMask options forwarded verbatim (Number/Date/RegExp masks, blocks,
   * definitions, `scale`, `thousandsSeparator`, etc.). Merged over `mask`.
   */
  imaskOptions?: Record<string, unknown>;
  /** Field label (Lato w600 s12). Omit to render no label. */
  label?: string;
  /** Current (masked) value when controlled. */
  value?: string;
  /** Placeholder text (gray60). */
  placeholder?: string;
  /** Fired with the accepted (masked) value on every change. */
  onAccept?: (value: string) => void;
  /** Fired when the mask is fully satisfied (complete). */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** Error state — switches the border to magenta. */
  error?: boolean;
  /** Error text rendered in an inline <ErrorMessage> beneath the field. */
  errorMessage?: string;
  /** Form field name (for native form submission). */
  name?: string;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

export function MaskedInput({
  mask,
  imaskOptions,
  label,
  value,
  placeholder,
  onAccept,
  onComplete,
  disabled,
  error = false,
  errorMessage,
  name,
  id,
  className,
  testId,
  'aria-label': ariaLabel,
}: MaskedInputProps) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const hasErrorMessage = error && !!errorMessage;

  // Merge the simple `mask` string with any passthrough options. The cast localizes the
  // react-imask union-type complexity to this wrapper (the public API stays string-friendly).
  const maskProps = { mask: mask ?? /.*/, ...imaskOptions } as Record<string, unknown>;

  return (
    <div className="flex flex-col items-stretch">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-8 font-sans text-s12 font-w600 tracking-givmo text-black"
        >
          {label}
        </label>
      )}
      <IMaskInput
        {...maskProps}
        id={inputId}
        name={name}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        aria-describedby={hasErrorMessage ? errorId : undefined}
        onAccept={(accepted: string) => onAccept?.(accepted)}
        onComplete={(completed: string) => onComplete?.(completed)}
        className={cn(
          'w-full rounded-all12 border bg-white p-12',
          'font-sans text-s16 font-w400 leading-h15 tracking-givmo text-black',
          'placeholder:text-gray60 outline-none transition-shadow',
          'border-neutral-500',
          'focus:border-blue focus:shadow-focus-glow',
          error && 'border-magenta focus:border-magenta',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...testIdProp(testId)}
      />
      {hasErrorMessage && (
        <ErrorMessage testId={testId ? `${testId}-error` : undefined}>
          <span id={errorId}>{errorMessage}</span>
        </ErrorMessage>
      )}
    </div>
  );
}
